/**
 * Разбор URL сгенерированного визуала на кабинет/воронку.
 * Путь визуала: <group>/<cabinet>/<funnel>/<file> (локальный /pipeline/visual?path=
 * или FTP). Кабинеты KSA/KMA/SVV — аккаунты WB.
 */
export interface VisualMeta {
  cabinet: string
  funnel: string
  url: string
}

// The renderer stores <group>/<cabinet>/<funnel>/<file>. Match the fixed-depth
// tail used by pipeline-visuals.service and frozen previews; FTP_PUBLIC_BASE may
// add any public prefix (including the historical /hdd).
const FUNNEL_PATH_DEPTH = 4

export function parseVisual(url: string): VisualMeta {
  const unknown = { cabinet: '', funnel: '', url }
  if (!url.trim()) return unknown
  try {
    const parsed = new URL(url.trim(), 'http://pipeline.invalid')
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return unknown
    const localVisual = /(?:^|\/)pipeline\/visual$/i.test(parsed.pathname)
    const path = localVisual ? parsed.searchParams.get('path') : parsed.pathname
    if (!path) return unknown
    const encodedSegments = path.split('/').filter(Boolean)
    if (encodedSegments.length < FUNNEL_PATH_DEPTH) return unknown
    const segments = encodedSegments.slice(-FUNNEL_PATH_DEPTH).map((segment) => decodeURIComponent(segment))
    if (segments.some((segment) => !segment || segment === '.' || segment === '..' || /[/\\]/.test(segment))) {
      return unknown
    }
    return { cabinet: segments[1].toUpperCase(), funnel: segments[2], url }
  } catch {
    return unknown
  }
}

export type MarketplaceId = 'WB' | 'OZON' | 'YM'

/**
 * Кабинет → маркетплейс. KSA/KMA/SVV — WB, KSA_YM — Я.Маркет (кабинет `ksa_ym`,
 * `mp_sellers.visual_path_token`, см. docs/SPEC_2026-09-13_YANDEX_MARKET.md §4.2).
 * OZON в этой таблице по-прежнему не заведён — падает в тот же безадресный
 * фолбэк `WB`, что и раньше; это не регресс задачи 2F2, а существовавший
 * прежде пробел (см. отчёт).
 */
const MARKETPLACE_BY_CABINET: Record<string, MarketplaceId> = {
  KSA: 'WB',
  KMA: 'WB',
  SVV: 'WB',
  KSA_YM: 'YM',
}

export function marketplaceOfCabinet(cabinet: string): MarketplaceId {
  return MARKETPLACE_BY_CABINET[cabinet.toUpperCase()] ?? 'WB'
}

/**
 * Тон бейджа площадки конвейера — по образцу `MARKETPLACE_BADGE_TONE` реестра
 * (`shared/marketplaces.ts`: wb=purple, ozon=blue, ym=yellow), но раскрашенный
 * тут же готовыми классами Tailwind, которые уже использовали `StageTable`
 * и `ApprovalStage` — чтобы визуально ничего не поменялось у WB/OZON.
 */
export const MARKETPLACE_ID_BADGE_CLASS: Record<MarketplaceId, string> = {
  WB: 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300',
  OZON: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  YM: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
}

/** Иерархия визуалов товаров: маркетплейс → кабинет → визуалы. */
export function groupVisualsByMarketplaceCabinet(
  products: { article: string; imageUrls: string[] }[]
): Map<MarketplaceId, Map<string, VisualMeta[]>> {
  const byMp = new Map<MarketplaceId, Map<string, VisualMeta[]>>()
  for (const p of products) {
    for (const url of p.imageUrls ?? []) {
      const v = parseVisual(url)
      const mp = marketplaceOfCabinet(v.cabinet)
      if (!byMp.has(mp)) byMp.set(mp, new Map())
      const byCab = byMp.get(mp)!
      if (!byCab.has(v.cabinet)) byCab.set(v.cabinet, [])
      byCab.get(v.cabinet)!.push(v)
    }
  }
  return byMp
}
